import moment from 'moment';
import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { IconButton } from '../../components/Button';
import { gql } from '../../graphql/generated';
import checkIcon from '../../icons/non-prefilled/16x16/check.svg';
import { miscToasts } from '../../toasts/toasts';
import { UIActions } from '../../ui/uiSlice';
import Logger from '../../utils/Logger';
import { formatVersionDate } from './VersionChangesDropdown';
import * as styles from './VersionNameForm.module.scss';

const VERSION_NAME_MAX_LENGTH = 100;

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const UPDATE_HISTORY_NAME_MUTATION = gql(/** @complexity 2 */ `
  mutation updateHistoryName($historyId: ID!, $name: String!) {
    updateHistory(historyId: $historyId, name: $name) {
      id
      name
    }
  }
`);

interface VersionNameFormProps {
  onReset: () => void;
  currentVersionName: string | null;
  historyInsertedAt: string | null;
  historyId: string;
}

const VersionNameForm = ({
  onReset,
  currentVersionName,
  historyInsertedAt,
  historyId,
}: VersionNameFormProps) => {
  const dispatch = useAppDispatch();
  const [updateHistoryName] = useMutation(UPDATE_HISTORY_NAME_MUTATION);
  const [versionName, setVersionName] = useState(currentVersionName ?? '');

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        const trimmedVersionName = versionName.trim();
        if (
          (currentVersionName == null && !trimmedVersionName) ||
          trimmedVersionName === currentVersionName
        ) {
          onReset();
          return;
        }

        onReset();

        try {
          await updateHistoryName({
            variables: {
              historyId,
              name: trimmedVersionName,
            },
            optimisticResponse: {
              updateHistory: {
                __typename: 'History',
                id: historyId,
                name: trimmedVersionName,
              },
            },
          });
        } catch (error) {
          Logger.error(error);

          dispatch(UIActions.pushToast(miscToasts.genericError));
        }
      }}
      className={styles.versionNameInputContainer}
    >
      <input
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onReset();
          }
        }}
        className={styles.versionNameInput}
        maxLength={VERSION_NAME_MAX_LENGTH}
        type="text"
        name="versionName"
        value={versionName}
        placeholder={
          (historyInsertedAt
            ? formatVersionDate(moment(historyInsertedAt))
            : null) ?? ''
        }
        onChange={(e) => setVersionName(e.target.value)}
        autoFocus
        aria-label="Version name"
      />
      <IconButton
        disabled={false}
        type="submit"
        aria-label="Save"
        size="sm"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onReset();
          }
        }}
      >
        {checkIcon}
      </IconButton>
    </form>
  );
};

export default VersionNameForm;
